import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Alert,
  Chip
} from '@mui/material';
import './MultiDayTimeSlotSelector.css';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { format, addDays, subDays, isSameDay, isPast, isToday } from 'date-fns';
import { TimeSlot, Vessel, MarinaSetting } from '../models/types';
import { checkAvailability } from '../firebase/services/reservationService';
import { getMarinaSettings } from '../firebase/services/marinaSettingsService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

interface TimeSlotInfo {
  id: string;
  startTime: string;
  checkInTime: string;
  available: boolean;
  price?: number;
}

interface DayTimeSlots {
  date: Date;
  dayName: string;
  timeSlots: TimeSlotInfo[];
  loading: boolean;
}

interface MultiDayTimeSlotSelectorProps {
  selectedDate: Date;
  vesselType: string;
  availableVessels: Vessel[];
  selectedDuration: number;
  onTimeSlotSelect: (date: Date, timeSlot: TimeSlot, price?: number, duration?: number) => void;
  onDateChange: (date: Date) => void;
  onDurationChange?: (duration: number) => void;
  durationOptions?: Array<{ hours: number; price: number; description?: string }>;
}

const MultiDayTimeSlotSelector: React.FC<MultiDayTimeSlotSelectorProps> = ({
  selectedDate,
  vesselType,
  availableVessels,
  selectedDuration,
  onTimeSlotSelect,
  onDateChange,
  onDurationChange,
  durationOptions = []
}) => {
  const [currentDays, setCurrentDays] = useState<DayTimeSlots[]>([]);
  const [marinaSettings, setMarinaSettings] = useState<MarinaSetting | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{date: Date, timeSlot: string} | null>(null);
  const [showFutureDates, setShowFutureDates] = useState(false);
  const [todayAvailableSlots, setTodayAvailableSlots] = useState(0);
  const [showDurationSelector, setShowDurationSelector] = useState(false);

  // Generate time slots for a day based on business hours and advance booking requirements
  const generateTimeSlots = (forDate: Date): Omit<TimeSlotInfo, 'available'>[] => {
    const slots: Omit<TimeSlotInfo, 'available'>[] = [];
    
    if (!marinaSettings) return slots;
    
    // Check if this is for today's date
    const today = new Date();
    const isToday = forDate.toDateString() === today.toDateString();
    
    // Get minimum advance booking time from marina settings
    const minAdvanceHours = marinaSettings.reservationRules?.minAdvanceBooking || 2;
    
    // Get business hours (simplified - using default business hours for now)
    // In a full implementation, you'd want to check seasonal hours and day-specific hours
    const openingHour = 8; // 8 AM
    const closingHour = 19; // 7 PM
    
    let startHour = openingHour;
    
    if (isToday) {
      // Calculate minimum booking time (current time + advance booking hours)
      const minBookingTime = new Date();
      minBookingTime.setHours(minBookingTime.getHours() + minAdvanceHours);
      
      // Round up to the next hour for cleaner time slots
      const minBookingHour = minBookingTime.getMinutes() > 0 
        ? minBookingTime.getHours() + 1 
        : minBookingTime.getHours();
      
      // Use the later of opening hour or minimum booking hour
      startHour = Math.max(openingHour, minBookingHour);
      
      console.log('Generating time slots for today:');
      console.log('Current time:', today.toLocaleTimeString());
      console.log('Min advance hours:', minAdvanceHours);
      console.log('Min booking time:', minBookingTime.toLocaleTimeString());
      console.log('Effective start hour:', startHour);
    }
    
    // Calculate the latest start hour based on duration
    // If duration is 3 hours and closing is 7pm, last start time should be 4pm
    const latestStartHour = closingHour - selectedDuration;
    
    // Don't generate slots if start hour is past the latest possible start hour
    if (startHour >= latestStartHour) {
      console.log('No time slots available - start hour', startHour, 'is past latest start hour', latestStartHour, 'for', selectedDuration, 'hour duration');
      return slots;
    }
    
    for (let hour = startHour; hour <= latestStartHour; hour++) {
      const endHour = hour + selectedDuration;
      
      // Only show slots that don't extend past closing
      if (endHour <= closingHour) {
        const startTime = `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`;
        const endTime = `${endHour > 12 ? endHour - 12 : endHour === 0 ? 12 : endHour}:00 ${endHour >= 12 ? 'PM' : 'AM'}`;
        
        // Calculate check-in time based on marina settings
        const checkInMinutes = marinaSettings.reservationRules?.checkInTimeMinutes || 15;
        const slotStartTime = new Date(forDate);
        slotStartTime.setHours(hour, 0, 0, 0);
        const checkInTime = new Date(slotStartTime.getTime() - (checkInMinutes * 60 * 1000));
        
        const checkInTimeFormatted = checkInTime.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        
        // Get actual pricing from duration options
        const durationOption = durationOptions.find(opt => opt.hours === selectedDuration);
        const actualPrice = durationOption ? durationOption.price : 150 * selectedDuration;
        
        slots.push({
          id: `${hour}:00`,
          startTime: `${startTime} - ${endTime}`,
          checkInTime: `Check In: ${checkInTimeFormatted}`,
          price: actualPrice
        });
      }
    }
    
    console.log(`Generated ${slots.length} time slots for ${forDate.toDateString()} (${selectedDuration}hr duration):`, slots.map(s => s.startTime));
    return slots;
  };

  // Check availability for a specific time slot on a specific date
  // LOGIC: Availability is based on VESSEL TYPE, not individual vessels
  // Example: 2 pontoons available
  // - Pontoon A rented 12-1pm
  // - Pontoon B rented 12-2pm  
  // - Attempting to book pontoon 12-1pm should FAIL because both pontoons are occupied during 12-1pm
  const checkTimeSlotAvailability = async (date: Date, hour: number): Promise<boolean> => {
    if (isPast(date) && !isToday(date)) return false;
    if (!availableVessels.length) return false;
    if (!marinaSettings) return false;

    // For today's date, check advance booking requirements
    if (isToday(date)) {
      const minAdvanceHours = marinaSettings.reservationRules?.minAdvanceBooking || 2;
      const now = new Date();
      const slotTime = new Date(date);
      slotTime.setHours(hour, 0, 0, 0);
      
      const minBookingTime = new Date(now);
      minBookingTime.setHours(minBookingTime.getHours() + minAdvanceHours);
      
      if (slotTime <= minBookingTime) {
        console.log(`Time slot ${hour}:00 is too soon - requires ${minAdvanceHours}hr advance booking`);
        return false;
      }
    }

    try {
      const dateString = format(date, 'yyyy-MM-dd');
      const endHour = hour + selectedDuration;
      const timeSlot = `${hour.toString().padStart(2, '0')}:00-${endHour.toString().padStart(2, '0')}:00` as TimeSlot;
      
      // VESSEL TYPE BASED AVAILABILITY CHECKING
      // Get ALL reservations for this vessel TYPE (not individual vessels) on this date
      const reservationsQuery = query(
        collection(db, 'reservations'),
        where('vesselType', '==', vesselType),
        where('date', '==', dateString),
        where('status', 'in', ['pending', 'confirmed'])
      );
      
      const reservationsSnapshot = await getDocs(reservationsQuery);
      
      // Count how many vessels of this TYPE are occupied during this time slot
      let occupiedVesselCount = 0;
      
      reservationsSnapshot.docs.forEach(doc => {
        const reservation = doc.data();
        
        // Check if this reservation's time slot overlaps with the requested time slot
        if (reservation.timeSlot && reservation.timeSlot.includes('-')) {
          const [existingStart, existingEnd] = reservation.timeSlot.split('-');
          const existingStartMinutes = convertTimeToMinutes(existingStart);
          const existingEndMinutes = convertTimeToMinutes(existingEnd);
          
          const requestedStartMinutes = hour * 60;
          const requestedEndMinutes = endHour * 60; // Use actual end time based on duration
          
          // Check for overlap - if any part of the existing reservation overlaps with requested time
          if (requestedStartMinutes < existingEndMinutes && requestedEndMinutes > existingStartMinutes) {
            occupiedVesselCount++;
            console.log(`Overlap found: existing ${reservation.timeSlot} overlaps with requested ${hour}:00-${endHour}:00`);
          }
        }
      });
      
      // Total available vessels of this TYPE
      const totalVesselsOfType = availableVessels.length;
      
      console.log(`Availability check for ${vesselType} at ${hour}:00-${endHour}:00 (${selectedDuration}hrs) on ${dateString}:`);
      console.log(`- Total ${vesselType} vessels: ${totalVesselsOfType}`);
      console.log(`- Occupied during this time: ${occupiedVesselCount}`);
      console.log(`- Available: ${occupiedVesselCount < totalVesselsOfType ? 'YES' : 'NO'}`);
      
      // Available ONLY if we have more vessels of this type than are currently occupied
      return occupiedVesselCount < totalVesselsOfType;
      
    } catch (error) {
      console.error('Error checking time slot availability:', error);
      return false;
    }
  };

  // Helper function to convert time string "HH:MM" to minutes since midnight
  const convertTimeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Load time slots - initially only for selected date, with option to show future dates
  const loadTimeSlots = async () => {
    if (!marinaSettings) {
      console.log('Marina settings not loaded yet, skipping time slot generation');
      return;
    }
    

    
    const today = new Date();
    
    // Determine which dates to show
    let datesToShow: Date[] = [];
    let dayNames: string[] = [];
    
    if (showFutureDates) {
      // Show selected date + next 2 days (or today + next 2 days if selected date is in the past)
      const startDate = isPast(selectedDate) && !isToday(selectedDate) ? today : selectedDate;
      datesToShow = [startDate, addDays(startDate, 1), addDays(startDate, 2)];
      dayNames = datesToShow.map((date, index) => {
        if (isToday(date)) return 'Today';
        if (index === 1 && isToday(addDays(today, 1))) return 'Tomorrow';
        return format(date, 'EEEE, MMM d');
      });
    } else {
      // Show only the selected date (or today if selected date is in the past)
      const dateToShow = isPast(selectedDate) && !isToday(selectedDate) ? today : selectedDate;
      datesToShow = [dateToShow];
      dayNames = [isToday(dateToShow) ? 'Today' : format(dateToShow, 'EEEE, MMM d')];
    }
    
    const newCurrentDays: DayTimeSlots[] = datesToShow.map((date, index) => ({
      date,
      dayName: dayNames[index],
      timeSlots: generateTimeSlots(date).map(slot => ({ ...slot, available: false })),
      loading: true
    }));

    setCurrentDays(newCurrentDays);

    // Check availability for each time slot on each day
    let todayAvailableCount = 0;
    setTodayAvailableSlots(0); // Reset today's count
    
    for (let dayIndex = 0; dayIndex < datesToShow.length; dayIndex++) {
      const date = datesToShow[dayIndex];
      const baseSlots = generateTimeSlots(date);
      
      const slotsWithAvailability = await Promise.all(
        baseSlots.map(async (slot) => {
          const hour = parseInt(slot.id.split(':')[0]);
          const available = await checkTimeSlotAvailability(date, hour);
          return { ...slot, available };
        })
      );

      // Count available slots for today (only if this date is actually today)
      if (isToday(date)) {
        todayAvailableCount = slotsWithAvailability.filter(slot => slot.available).length;
        setTodayAvailableSlots(todayAvailableCount);
      }

      setCurrentDays(prev => 
        prev.map((day, index) => 
          index === dayIndex 
            ? { ...day, timeSlots: slotsWithAvailability, loading: false }
            : day
        )
      );
    }
    
    console.log(`Today has 
      {todayAvailableCount} available time slots`);
  };

  // Load marina settings
  useEffect(() => {
    const fetchMarinaSettings = async () => {
      try {
        const settings = await getMarinaSettings();
        setMarinaSettings(settings);
      } catch (error) {
        console.error('Error fetching marina settings:', error);
      }
    };
    fetchMarinaSettings();
  }, []);

  // Load time slots when vessels, marina settings, view mode, duration, or selected date changes
  useEffect(() => {
    if (availableVessels.length > 0 && marinaSettings) {
      loadTimeSlots();
    }
  }, [availableVessels, marinaSettings, showFutureDates, selectedDuration, selectedDate]);

  // Handle showing future dates
  const handleShowFutureDates = () => {
    setShowFutureDates(true);
  };

  // Handle going back to today only
  const handleShowTodayOnly = () => {
    setShowFutureDates(false);
  };

  // Handle duration change - mobile-optimized
  const handleDurationChange = (duration: number) => {
    if (onDurationChange) {
      onDurationChange(duration);
    }
  };

  // Handle time slot selection
  const handleTimeSlotClick = (date: Date, slot: TimeSlotInfo) => {
    if (!slot.available) return;

    const hour = parseInt(slot.id.split(':')[0]);
    const endHour = hour + selectedDuration;
    const timeSlot = `${hour.toString().padStart(2, '0')}:00-${endHour.toString().padStart(2, '0')}:00` as TimeSlot;
    
    setSelectedTimeSlot({ date, timeSlot: slot.id });
    
    // Update the parent component's selected date
    onDateChange(date);
    onTimeSlotSelect(date, timeSlot, slot.price, selectedDuration);
  };

  // Render a single day column
  const renderDayColumn = (dayData: DayTimeSlots, isSelected: boolean) => {
    const { date, dayName, timeSlots, loading } = dayData;
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    // Determine grid size based on number of days being shown
    const gridSize = showFutureDates ? 4 : 12;
    
    return (
      <Grid item xs={12} md={gridSize} key={date.toISOString()}>
        <Paper 
          elevation={isSelected ? 3 : 1} 
          sx={{ 
            p: 1, 
            height: '100%',
            backgroundColor: isSelected ? 'primary.light' : 'background.paper',
            border: isSelected ? 2 : 0,
            borderColor: 'primary.main'
          }}
        >
          {showFutureDates && (
            <Box textAlign="center" mb={1}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                {isToday ? 'Today' : format(date, 'EEEE, MMM d')}
              </Typography>
            </Box>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          ) : timeSlots.length === 0 ? (
            <Box textAlign="center" py={2}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                No time slots available
              </Typography>
            </Box>
          ) : (
            <Box>
              {timeSlots.map((slot) => {
                const isSlotSelected = selectedTimeSlot?.date && 
                  isSameDay(selectedTimeSlot.date, date) && 
                  selectedTimeSlot.timeSlot === slot.id;

                return (
                  <Paper
                    key={slot.id}
                    elevation={isSlotSelected ? 2 : 0}
                    sx={{
                      p: 1,
                      mb: 0.3,
                      cursor: slot.available ? 'pointer' : 'not-allowed',
                      backgroundColor: isSlotSelected 
                        ? 'primary.main' 
                        : slot.available 
                          ? 'background.paper' 
                          : 'grey.100',
                      color: isSlotSelected 
                        ? 'primary.contrastText'
                        : slot.available 
                          ? 'text.primary' 
                          : 'text.disabled',
                      border: 1,
                      borderColor: isSlotSelected 
                        ? 'primary.main'
                        : slot.available 
                          ? 'grey.300' 
                          : 'grey.200',
                      '&:hover': slot.available ? {
                        backgroundColor: isSlotSelected ? 'primary.dark' : 'grey.50',
                        borderColor: 'primary.main'
                      } : {},
                      minHeight: 'auto'
                    }}
                    onClick={() => slot.available && handleTimeSlotClick(date, slot)}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box flex={1}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                          {slot.startTime}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" alignItems="center" gap={1}>
                        {slot.available && slot.price && (
                          <Chip 
                            label={`$${slot.price}`} 
                            size="small" 
                            color={isSlotSelected ? "secondary" : "primary"}
                            variant={isSlotSelected ? "filled" : "outlined"}
                            sx={{ height: 24, '& .MuiChip-label': { fontSize: '0.7rem', px: 1 } }}
                          />
                        )}
                        {slot.available && (
                          <ChevronRightIcon fontSize="small" />
                        )}
                      </Box>
                    </Box>
                    
                    {!slot.available && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                        Sold Out
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
        </Paper>
      </Grid>
    );
  };

  return (
    <Box className="multi-day-time-slot-selector">
      {/* Duration selector and info */}
      <Box 
        mb={2} 
        p={2} 
        sx={{ backgroundColor: 'grey.50', borderRadius: 1 }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={showDurationSelector ? 2 : 0}>
          <Typography variant="subtitle2" fontWeight="bold">
            Duration: {selectedDuration} hour rental
          </Typography>
          
          {/* Change Duration Link - Both Mobile and Desktop */}
          <Box>
            {onDurationChange && durationOptions.length > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={() => setShowDurationSelector(!showDurationSelector)}
                sx={{ 
                  color: 'primary.main',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  minWidth: 'auto',
                  p: 0.5
                }}
              >
                Change Duration
              </Button>
            )}
          </Box>
        </Box>
        
        {/* Duration Options - Hidden by default, toggle on both mobile and desktop */}
        {onDurationChange && durationOptions.length > 0 && (
          <Box 
            sx={{ 
              display: showDurationSelector ? 'block' : 'none'
            }}
          >
            <Box 
              display="flex" 
              gap={1}
              flexWrap="wrap"
              sx={{ 
                width: '100%',
                mt: 1
              }}
            >
              {durationOptions.map((option) => (
                <Chip
                  key={option.hours}
                  label={`${option.hours}hr - $${option.price}`}
                  clickable
                  color={selectedDuration === option.hours ? "primary" : "default"}
                  variant={selectedDuration === option.hours ? "filled" : "outlined"}
                  size="medium"
                  onClick={() => {
                    handleDurationChange(option.hours);
                    // Close selector after selection
                    setShowDurationSelector(false);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    handleDurationChange(option.hours);
                    // Close selector after selection
                    setShowDurationSelector(false);
                  }}
                  sx={{
                    minHeight: { xs: 48, sm: 32 },
                    fontSize: { xs: '1rem', sm: '0.875rem' },
                    '& .MuiChip-label': {
                      px: { xs: 2, sm: 1.5 },
                      py: { xs: 1, sm: 0.5 },
                      fontSize: { xs: '0.9rem', sm: '0.8rem' }
                    },
                    '&:active': {
                      transform: 'scale(0.95)',
                      transition: 'transform 0.1s ease'
                    },
                    cursor: 'pointer',
                    userSelect: 'none',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Compact time slots */}
      <Box>
        {currentDays.length > 0 && (
          <Box>
            {currentDays[0].loading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={20} />
              </Box>
            ) : currentDays[0].timeSlots.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                No time slots available for {selectedDuration} hour rental on {format(currentDays[0].date, 'EEEE, MMM d')}
              </Alert>
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Available times for {format(currentDays[0].date, 'EEEE, MMM d')}:
                </Typography>
                
                <Grid container spacing={0.5}>
                  {currentDays[0].timeSlots.map((slot) => {
                    const isSlotSelected = selectedTimeSlot?.date && 
                      isSameDay(selectedTimeSlot.date, currentDays[0].date) && 
                      selectedTimeSlot.timeSlot === slot.id;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={slot.id}>
                        <Paper
                          elevation={isSlotSelected ? 2 : 0}
                          sx={{
                            p: 1,
                            cursor: slot.available ? 'pointer' : 'not-allowed',
                            backgroundColor: isSlotSelected 
                              ? 'primary.main' 
                              : slot.available 
                                ? 'background.paper' 
                                : 'grey.100',
                            color: isSlotSelected 
                              ? 'primary.contrastText'
                              : slot.available 
                                ? 'text.primary' 
                                : 'text.disabled',
                            border: 1,
                            borderColor: isSlotSelected 
                              ? 'primary.main'
                              : slot.available 
                                ? 'grey.300' 
                                : 'grey.200',
                            '&:hover': slot.available ? {
                              backgroundColor: isSlotSelected ? 'primary.dark' : 'grey.50',
                              borderColor: 'primary.main'
                            } : {},
                            minHeight: 60
                          }}
                          onClick={() => slot.available && handleTimeSlotClick(currentDays[0].date, slot)}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                      <Box>
                            <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                              {slot.startTime}
                            </Typography>
                            {slot.available && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {slot.checkInTime}
                              </Typography>
                            )}
                            {!slot.available && (
                              <Typography variant="caption" color="error" sx={{ fontSize: '0.65rem' }}>
                                Unavailable
                              </Typography>
                            )}
                          </Box>
                            
                            {slot.available && slot.price && (
                              <Typography variant="caption" color="success.main" fontWeight="bold">
                                ${slot.price}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Box>
        )}
        
        {/* Future dates option */}
        {!showFutureDates && currentDays.length > 0 && currentDays[0].timeSlots.filter(slot => slot.available).length === 0 && (
          <Box textAlign="center" mt={2}>
            <Button 
              variant="outlined" 
              onClick={handleShowFutureDates}
              size="small"
            >
              Check Future Dates
            </Button>
          </Box>
        )}
        
        {showFutureDates && currentDays.length > 1 && (
          <Box mt={3}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Future dates:
            </Typography>
            <Grid container spacing={1}>
              {currentDays.slice(1).map((dayData) => 
                renderDayColumn(dayData, false)
              )}
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MultiDayTimeSlotSelector; 